import type { StatusVariant } from '../../utils/statusUtils';

/**
 * The material ledger (MT-V04) on the client: what the API returns, the filters the page keeps in
 * its URL, and the small rules for showing a row. The server owns the data; nothing here works a
 * level out.
 */

export type LedgerMovementType = 'CONSUME' | 'TRANSFER' | 'RECEIPT' | 'COUNT' | 'DISPOSAL' | 'RETURN';
export type LedgerSource = 'WRAP' | 'MRAP_MOBILE' | 'IMPORT' | 'TRUECONTEXT';

export interface LedgerBinRef {
  binId: string;
  binCode: string;
  locationName: string;
  siteId: string;
  siteName: string;
  siteNumber: string | null;
}

export interface LedgerEntry {
  id: string;
  type: LedgerMovementType;
  occurredAt: string;
  createdAt: string;
  quantity: number;
  unitOfMeasure: string | null;
  expectedQuantity: number | null;
  countMethod: string | null;
  material: { id: string; code: string | null; label: string };
  from: LedgerBinRef | null;
  to: LedgerBinRef | null;
  reason: string;
  source: LedgerSource;
  recordedBy: string | null;
  task: { id: string; label: string } | null;
  vendor: string | null;
  flaggedNegative: boolean;
  costCarried: boolean;
  unitCost: number | null;
  destinationUnitCost: number | null;
  balanceAfter: number | null;
}

export interface LedgerPage {
  entries: LedgerEntry[];
  total: number;
  page: number;
  pageSize: number;
  canSeeCost: boolean;
}

export const LEDGER_TYPES: { value: LedgerMovementType; label: string; variant: StatusVariant }[] = [
  { value: 'RECEIPT', label: 'Receipt', variant: 'success' },
  { value: 'CONSUME', label: 'Consume', variant: 'primary' },
  { value: 'TRANSFER', label: 'Transfer', variant: 'info' },
  { value: 'COUNT', label: 'Count', variant: 'secondary' },
  { value: 'RETURN', label: 'Return', variant: 'success' },
  { value: 'DISPOSAL', label: 'Disposal', variant: 'warning' },
];

export const LEDGER_TYPE = Object.fromEntries(LEDGER_TYPES.map((type) => [type.value, type])) as Record<
  LedgerMovementType,
  (typeof LEDGER_TYPES)[number]
>;

export const LEDGER_SOURCES: { value: LedgerSource; label: string }[] = [
  { value: 'WRAP', label: 'WRAP' },
  { value: 'MRAP_MOBILE', label: 'Mobile app' },
  { value: 'IMPORT', label: 'Import' },
  { value: 'TRUECONTEXT', label: 'TrueContext' },
];

export const LEDGER_SOURCE_LABEL = Object.fromEntries(LEDGER_SOURCES.map((s) => [s.value, s.label])) as Record<LedgerSource, string>;

/** The page's filters, as kept in its URL. Dates are calendar days (YYYY-MM-DD), both inclusive. */
export interface LedgerFilters {
  from: string;
  to: string;
  siteId: string;
  binId: string;
  materialId: string;
  types: LedgerMovementType[];
  sources: LedgerSource[];
  flaggedOnly: boolean;
}

export const EMPTY_LEDGER_FILTERS: LedgerFilters = {
  from: '',
  to: '',
  siteId: '',
  binId: '',
  materialId: '',
  types: [],
  sources: [],
  flaggedOnly: false,
};

const isType = (value: string): value is LedgerMovementType => LEDGER_TYPES.some((type) => type.value === value);
const isSource = (value: string): value is LedgerSource => LEDGER_SOURCES.some((source) => source.value === value);
const DAY = /^\d{4}-\d{2}-\d{2}$/;

export function filtersFromParams(params: URLSearchParams): LedgerFilters {
  const day = (key: string) => (DAY.test(params.get(key) ?? '') ? (params.get(key) as string) : '');
  const csv = (key: string) => (params.get(key) ?? '').split(',').filter(Boolean);
  return {
    from: day('from'),
    to: day('to'),
    siteId: params.get('siteId') ?? '',
    binId: params.get('binId') ?? '',
    materialId: params.get('materialId') ?? '',
    types: csv('type').filter(isType),
    sources: csv('source').filter(isSource),
    flaggedOnly: params.get('flagged') === 'true',
  };
}

/** The URL for a filter set; empty values are left out so a shared link stays short. */
export function paramsFromFilters(filters: LedgerFilters, page = 1): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.siteId) params.set('siteId', filters.siteId);
  if (filters.binId) params.set('binId', filters.binId);
  if (filters.materialId) params.set('materialId', filters.materialId);
  if (filters.types.length) params.set('type', filters.types.join(','));
  if (filters.sources.length) params.set('source', filters.sources.join(','));
  if (filters.flaggedOnly) params.set('flagged', 'true');
  if (page > 1) params.set('page', String(page));
  return params;
}

/** Local midnight at the start of a calendar day, as the instant the API filters on. */
export function startOfDay(day: string): string {
  const [year, month, date] = day.split('-').map(Number);
  return new Date(year, month - 1, date).toISOString();
}

/** The API's `to` is exclusive: the start of the day after the last day wanted. */
export function startOfNextDay(day: string): string {
  const [year, month, date] = day.split('-').map(Number);
  return new Date(year, month - 1, date + 1).toISOString();
}

/** The API query for a filter set: days become instants in the browser's (the user's) time zone. */
export function apiQuery(filters: LedgerFilters, extra: Record<string, string> = {}): URLSearchParams {
  const params = new URLSearchParams(extra);
  if (filters.from) params.set('from', startOfDay(filters.from));
  if (filters.to) params.set('to', startOfNextDay(filters.to));
  if (filters.siteId) params.set('siteId', filters.siteId);
  if (filters.binId) params.set('binId', filters.binId);
  if (filters.materialId) params.set('materialId', filters.materialId);
  if (filters.types.length) params.set('type', filters.types.join(','));
  if (filters.sources.length) params.set('source', filters.sources.join(','));
  if (filters.flaggedOnly) params.set('flagged', 'true');
  return params;
}

export function activeLedgerFilterCount(filters: LedgerFilters): number {
  return (
    (filters.from ? 1 : 0) +
    (filters.to ? 1 : 0) +
    (filters.siteId ? 1 : 0) +
    (filters.binId ? 1 : 0) +
    (filters.materialId ? 1 : 0) +
    filters.types.length +
    filters.sources.length +
    (filters.flaggedOnly ? 1 : 0)
  );
}

/** Today and the first of this month as calendar days: the export's starting window. */
export function thisMonth(now = new Date()): { from: string; to: string } {
  const pad = (n: number) => String(n).padStart(2, '0');
  const day = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  return { from: day(new Date(now.getFullYear(), now.getMonth(), 1)), to: day(now) };
}

/** Longest export window, matching the server's limit. */
export const EXPORT_MAX_DAYS = 366;

/** Why an export window cannot be sent, or null when it can. */
export function exportWindowProblem(from: string, to: string): string | null {
  if (!from || !to) return 'Choose a from and a to date.';
  if (from > to) return 'The from date is after the to date.';
  const days = (new Date(startOfNextDay(to)).getTime() - new Date(startOfDay(from)).getTime()) / 86_400_000;
  if (days > EXPORT_MAX_DAYS + 1) return `An export covers at most ${EXPORT_MAX_DAYS} days.`;
  return null;
}

/**
 * How a row moved the bin being looked at: + for into it, − for out of it, a count sets it.
 * Null when the list is not narrowed to one bin, where a movement has no single direction.
 */
export function directionFor(entry: LedgerEntry, binId: string): 'in' | 'out' | 'set' | null {
  if (!binId) return null;
  if (entry.type === 'COUNT') return 'set';
  if (entry.to?.binId === binId) return 'in';
  if (entry.from?.binId === binId) return 'out';
  return null;
}

export const siteLabel = (ref: LedgerBinRef) => (ref.siteNumber ? `${ref.siteName} #${ref.siteNumber}` : ref.siteName);

/** The bin a filtered list is about, named from any row that touches it. */
export function binOf(entries: LedgerEntry[], binId: string): LedgerBinRef | null {
  for (const entry of entries) {
    if (entry.from?.binId === binId) return entry.from;
    if (entry.to?.binId === binId) return entry.to;
  }
  return null;
}
